import {render} from 'preact';

import {hideNode, showNode} from "../../utils/dom-utils.js";
import {BaseComponent} from "../../base.component.js";

const TooltipBubble = ({content}) =>
  <div>
    <div className="ab-tooltip-arrow">
      <div className="ab-arrow" />
    </div>
    <div className="ab-tooltip-bubble">
      <div className="ab-content">
        {content}
      </div>
    </div>
  </div>;

/**
 * Virtual component adapted from old badge code.
 * Can be used to show tooltips when used as parent to content.
 */
class AbTooltip extends BaseComponent {

  /** @type {HTMLElement} */
  _tooltipBubbleRoot;

  /** @type {HTMLElement} */
  _target;

  shown = false;

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    super.componentDidMount();

    // Create the bubble's root
    this._tooltipBubbleRoot = document.createElement('div');
    this._tooltipBubbleRoot.classList.add("ab-tooltip-bubble-root");
    this._tooltipBubbleRoot.style.zIndex = this.context.composition.parts.tooltips.zIndex.toString(10)

    hideNode(this._tooltipBubbleRoot);

    // Mount the bubble in its root
    this.context.rootElement.appendChild(this._tooltipBubbleRoot);
    this.refresh();

    // On window scroll adjust the tooltip's position
    window.addEventListener('scroll', () => this.updateBubblePosition());
  }

  componentDidUpdate() {
    this.refresh();
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this._tooltipBubbleRoot.parentNode.removeChild(this._tooltipBubbleRoot);
  }

  setTarget(target) {
    this._target = target;
  }

  refresh() {
    render(<TooltipBubble content={this.props.content} />, this._tooltipBubbleRoot);
  }

  show() {
    this.shown = true;
    showNode(this._tooltipBubbleRoot, 'flex');
    this.updateBubblePosition();
  }

  hide({ delayWhileHovering = false } = {}) {
    if (delayWhileHovering) {
      if (this._tooltipBubbleRoot.matches(':hover')) {
        // only hide this tooltip once the mouse went outside it
        const listener = () => {
          // need to re-check hover state, since this can trigger among inner tooltip elements
          if (!this._tooltipBubbleRoot.matches(':hover')) {
            this._tooltipBubbleRoot.removeEventListener('mouseout', listener);
            this.shown = false;
            hideNode(this._tooltipBubbleRoot);
          }
        };
        this._tooltipBubbleRoot.addEventListener('mouseout', listener);
        return;
      }
    }

    this.shown = false;
    hideNode(this._tooltipBubbleRoot);
  }

  updateBubblePosition() {
    const thresholdVertical = 128; // < px where a different vertical position is searched
    const thresholdHorizontal = 96; // < px where a different horizontal position is searched
    const distance = 10; // the distance to show the popup at from the target margin

    if (!this._tooltipBubbleRoot || !this._target) return;

    let rootElement = this.context.rootElement;
    if (!rootElement.getBoundingClientRect) {
      rootElement = rootElement.host; // shadow dom - the rootElement is just a fragment
    }

    const posRoot = rootElement.getBoundingClientRect();
    const posTarget = this._target.getBoundingClientRect();
    posTarget.midX = (posTarget.left + posTarget.right) / 2;
    posTarget.midY = (posTarget.top + posTarget.bottom) / 2;

    this._reset();

    if (this._fitsHorizontally(posTarget, thresholdHorizontal)) {
      // top/bottom tooltip, preferred if there is space
      this._tooltipBubbleRoot.style.left = `${posTarget.left - posRoot.left + posTarget.width / 2}px`;
      if (posTarget.midY < thresholdVertical) {
        this._snapToTop(posTarget, posRoot, distance);
      } else {
        this._snapToBottom(posTarget, posRoot, distance);
      }
    } else {
      // left/right tooltip, when space is limited
      this._tooltipBubbleRoot.style.top = `${posTarget.top - posRoot.top + posTarget.height / 2}px`;
      if (posTarget.left < thresholdHorizontal) {
        this._snapToLeft(posTarget, posRoot, distance);
      } else {
        this._snapToRight(posTarget, posRoot, distance);
      }
    }
  }

  _reset() {
    this._tooltipBubbleRoot.style.top = '';
    this._tooltipBubbleRoot.style.bottom = '';
    this._tooltipBubbleRoot.style.left = '';
    this._tooltipBubbleRoot.style.right = '';
  }

  _fitsHorizontally(posTarget, thresholdHorizontal) {
    const documentWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    return posTarget.midX > thresholdHorizontal && posTarget.midX < (documentWidth - thresholdHorizontal);
  }

  _snapToTop(posTarget, posRoot, distance) {
    this._tooltipBubbleRoot.style.top = `${posTarget.top - posRoot.top + posTarget.height + distance}px`;
    this._tooltipBubbleRoot.setAttribute('data-tooltip-snap', 'top');
  }

  _snapToBottom(posTarget, posRoot, distance) {
    this._tooltipBubbleRoot.style.bottom = `${posRoot.bottom - posTarget.bottom + posTarget.height + distance}px`;
    this._tooltipBubbleRoot.setAttribute('data-tooltip-snap', 'bottom');
  }

  _snapToLeft(posTarget, posRoot, distance) {
    this._tooltipBubbleRoot.style.left = `${posTarget.left - posRoot.left + posTarget.width + distance}px`;
    this._tooltipBubbleRoot.setAttribute('data-tooltip-snap', 'left');
  }

  _snapToRight(posTarget, posRoot, distance) {
    this._tooltipBubbleRoot.style.right = `${posRoot.right - posTarget.right + posTarget.width + distance}px`;
    this._tooltipBubbleRoot.setAttribute('data-tooltip-snap', 'right');
  }

}

export default AbTooltip;
